import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import {
  TEMPLATE_DATA,
  TEMPLATE_NAME,
} from "../../facility-management-screen/constants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";
import EventEHRTabs from "../../../component-lib/Molecules/tabs/EventEHRTabs";
import { plusIcon, uploadCSV } from "../../../assets";
import { convertFileToBase64 } from "../../../utils/convertFileToBase64";
import { uploadTemplateData } from "../SettingsSaga";

interface TemplateDialogProps {
  isOpen: boolean;
  handleData: (data: Record<string, unknown>) => void;
  handleClose: () => void;
  isEdit?: boolean;
  template?: string;
  defaultTemplateData: Record<string, unknown>;
  templateType?: string; // Template type: "Note", "Message", "Email"
  isImporting?: boolean;
  importMessage?: string | null;
  existingTemplateNames?: string[]; // previously-created (custom) template names
}

export default function TemplateDialog({
  isOpen = false,
  handleClose,
  handleData,
  isEdit = false,
  template,
  defaultTemplateData,
  templateType,
  isImporting = false,
  importMessage = null,
  existingTemplateNames = [],
}: TemplateDialogProps) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = React.useState("single");
  const [isCreatingNew, setIsCreatingNew] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  // Build the "Template Name" dropdown groups: the 3 always-present defaults,
  // and any previously-created custom names (excluding ones equal to a default).
  const templateNameGroups = React.useMemo(() => {
    const defaultValues = new Set(TEMPLATE_NAME.map((opt) => opt.value));
    const customOptions = Array.from(new Set(existingTemplateNames))
      .filter((name) => name && !defaultValues.has(name))
      .map((name) => ({ label: name, value: name }));

    return [
      { label: "Default Templates", options: TEMPLATE_NAME },
      ...(customOptions.length
        ? [{ label: "Custom Templates", options: customOptions }]
        : []),
    ];
  }, [existingTemplateNames]);

  // Determine template type - default to "Note" for Discharge Instructions
  const getTemplateType = () => {
    if (templateType) return templateType;
    if (template === "Discharge Instructions") return "Note";
    return "Note"; // Default fallback
  };

  const dialogHeading = isEdit
    ? template === "Discharge Instructions"
      ? "Edit Discharge Instructions"
      : `Edit ${template} Template`
    : template === "Discharge Instructions"
      ? "Add Discharge Instructions"
      : `Add ${template} Template`;

  const validationSchema = Yup.object().shape({
    [TEMPLATE_DATA?.TEMPLATE_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        template === "Discharge Instructions"
          ? "Instruction title can only contain characters"
          : "Template name can only contain characters",
      )
      .required(
        template === "Discharge Instructions"
          ? "Instruction title is required"
          : "Template name is required",
      ),

    [TEMPLATE_DATA?.DESCRIPTION]: Yup.string().required(
      "Description is required",
    ),
  });

  const bulkImportValidationSchema = Yup.object().shape({
    // File validation handled separately
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFileError("No file selected.");
      return;
    }

    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      setFileError("Only CSV, XLS, and XLSX files are allowed.");
      return;
    }

    setSelectedFile(file);
    setFileError(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      const allowedTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      const allowedExtensions = [".csv", ".xls", ".xlsx"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setFileError("Only CSV, XLS, and XLSX files are allowed.");
        return;
      }

      setSelectedFile(file);
      setFileError(null);
    }
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      {
        name: "Test",
        description: "Template 1,This is a test description for template 1",
      },
      {
        name: "Test",
        description:
          "Template 2,This is another test description for template 2",
      },
      {
        name: "Emergency Note Template",
        description: "Template for emergency visit notes",
      },
      {
        name: "Follow-up Note Template",
        description: "Template for follow-up visit documentation",
      },
      {
        name: "Transfer Note Template",
        description: "Template for patient transfer documentation",
      },
      {
        name: "Discharge Note Template",
        description: "Template for patient discharge notes",
      },
      {
        name: "Initial Assessment Template",
        description: "Template for initial patient assessment",
      },
      {
        name: "Progress Note Template",
        description: "Template for daily progress notes",
      },
      {
        name: "Procedure Note Template",
        description: "Template for procedure documentation",
      },
      {
        name: "Consultation Note Template",
        description: "Template for consultation notes",
      },
    ];

    // Create Excel XML format (SpreadsheetML) - Excel will recognize this as .xlsx
    let xmlContent = '<?xml version="1.0"?>\n';
    xmlContent += '<?mso-application progid="Excel.Sheet"?>\n';
    xmlContent +=
      '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
    xmlContent += ' xmlns:o="urn:schemas-microsoft-com:office:office"\n';
    xmlContent += ' xmlns:x="urn:schemas-microsoft-com:office:excel"\n';
    xmlContent += ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"\n';
    xmlContent += ' xmlns:html="http://www.w3.org/TR/REC-html40">\n';
    xmlContent += '<Worksheet ss:Name="Sheet1">\n';
    xmlContent += "<Table>\n";

    // Add header row
    xmlContent += "<Row>\n";
    xmlContent += '<Cell><Data ss:Type="String">name</Data></Cell>\n';
    xmlContent += '<Cell><Data ss:Type="String">description</Data></Cell>\n';
    xmlContent += "</Row>\n";

    // Add data rows
    sampleData.forEach((row) => {
      xmlContent += "<Row>\n";
      xmlContent += `<Cell><Data ss:Type="String">${escapeXml(row.name)}</Data></Cell>\n`;
      xmlContent += `<Cell><Data ss:Type="String">${escapeXml(row.description)}</Data></Cell>\n`;
      xmlContent += "</Row>\n";
    });

    xmlContent += "</Table>\n";
    xmlContent += "</Worksheet>\n";
    xmlContent += "</Workbook>";

    // Helper function to escape XML special characters
    function escapeXml(unsafe: string): string {
      return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    }

    // Add UTF-8 BOM for proper Excel encoding
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + xmlContent], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "discharge-instruction.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBulkImportSubmit = async () => {
    if (!selectedFile) {
      setFileError("Please select a file to import");
      return;
    }

    setIsUploading(true);
    setFileError(null);

    try {
      // Convert file to base64
      const base64Data = await convertFileToBase64(selectedFile);

      // Prepare payload
      const payload = {
        file: base64Data,
        type: getTemplateType(),
      };

      // Dispatch the upload action
      dispatch(uploadTemplateData(payload));
    } catch (error) {
      console.error("Error converting file to base64:", error);
      setFileError("Failed to process file. Please try again.");
      setIsUploading(false);
    }
  };

  // Reset states when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setActiveTab("single");
      setSelectedFile(null);
      setFileError(null);
      setIsDragOver(false);
      setIsUploading(false);
      setIsCreatingNew(false);
    }
  }, [isOpen]);

  console.log("defaultTemplateData", defaultTemplateData);

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      // sx={{
      //   "& .MuiDialog-paper": {
      //     height: "600px", // Adjust the height
      //     maxHeight: "90vh", // Prevent excessive height
      //   },
      // }}
    >
      <Formik
        initialValues={{
          [TEMPLATE_DATA?.TEMPLATE_NAME]: `${defaultTemplateData?.templateName || ""}`,
          [TEMPLATE_DATA?.DESCRIPTION]: `${defaultTemplateData?.description || ""}`,
          // [TEMPLATE_DATA?.DESCRIPTION]: `${defaultTemplateData?.description || ""}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                {template === "Discharge Instructions" && !isEdit ? (
                  <>
                    <EventEHRTabs
                      defaultSelectedValue={activeTab}
                      tabList={[
                        { label: "Add Single Instruction", value: "single" },
                        { label: "Import Bulk Instructions", value: "bulk" },
                      ]}
                      onChangeTab={(tab) => setActiveTab(tab)}
                    />
                    <Box sx={{ mt: 2 }}>
                      {activeTab === "single" && (
                        <div
                          style={{
                            overflow: "auto",
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: "16px",
                          }}
                        >
                          <div className="pt-4">
                            <Input
                              textFieldLabel="Instruction Title"
                              placeholder="Enter instruction title"
                              size="small"
                              name={TEMPLATE_DATA.TEMPLATE_NAME}
                              value={values[TEMPLATE_DATA.TEMPLATE_NAME]}
                              onChangeCb={(e) => {
                                setFieldValue(
                                  TEMPLATE_DATA.TEMPLATE_NAME,
                                  e.target.value,
                                );
                              }}
                              onBlur={handleBlur}
                              error={
                                touched[TEMPLATE_DATA.TEMPLATE_NAME] &&
                                Boolean(errors[TEMPLATE_DATA.TEMPLATE_NAME])
                              }
                              helperText={
                                touched[TEMPLATE_DATA.TEMPLATE_NAME]
                                  ? errors[TEMPLATE_DATA.TEMPLATE_NAME]
                                  : undefined
                              }
                            />
                          </div>

                          <Input
                            textFieldLabel="Description"
                            multiline={true}
                            rows={4}
                            maxWords={250}
                            size="small"
                            placeholder="Enter your instruction description here"
                            name={TEMPLATE_DATA.DESCRIPTION}
                            value={values[TEMPLATE_DATA.DESCRIPTION]}
                            onChangeCb={(e) => {
                              setFieldValue(
                                TEMPLATE_DATA.DESCRIPTION,
                                e.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched[TEMPLATE_DATA.DESCRIPTION] &&
                              Boolean(errors[TEMPLATE_DATA.DESCRIPTION])
                            }
                            helperText={
                              touched[TEMPLATE_DATA.DESCRIPTION]
                                ? errors[TEMPLATE_DATA.DESCRIPTION]
                                : undefined
                            }
                          />
                        </div>
                      )}
                      {activeTab === "bulk" && (
                        <Formik
                          initialValues={{ file: null }}
                          validationSchema={bulkImportValidationSchema}
                          enableReinitialize
                          onSubmit={() => {}}
                        >
                          {() => (
                            <Form>
                              <div className="w-full flex flex-col gap-6 rounded-lg">
                                {/* FILE UPLOAD SECTION */}
                                <div>
                                  <div className="flex flex-col gap-4">
                                    {/* CSV Upload Area */}
                                    <Box textAlign="start">
                                      <button
                                        type="button"
                                        className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer w-full bg-transparent ${
                                          isDragOver
                                            ? "border-primary-main bg-primary-light"
                                            : "border-neutral-30 hover:border-primary-main"
                                        }`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() =>
                                          document
                                            .getElementById(
                                              "bulk-instruction-upload",
                                            )
                                            ?.click()
                                        }
                                      >
                                        <img
                                          src={uploadCSV}
                                          alt="Upload CSV"
                                          className="mb-4 w-24 h-24"
                                        />
                                        <Typography
                                          variant="h6"
                                          className="text-primary-main underline cursor-pointer"
                                        >
                                          Upload File
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          className="text-neutral-60 mt-2"
                                        >
                                          Click to select or drag and drop your
                                          file here
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          className="text-neutral-50 mt-1"
                                        >
                                          Supported formats: CSV, XLS, XLSX
                                        </Typography>
                                      </button>
                                      <input
                                        type="file"
                                        accept=".csv,.xls,.xlsx"
                                        id="bulk-instruction-upload"
                                        hidden
                                        onChange={handleFileChange}
                                      />

                                      {fileError && (
                                        <Typography
                                          color="error"
                                          className="mt-2"
                                        >
                                          {fileError}
                                        </Typography>
                                      )}

                                      {importMessage && (
                                        <Typography
                                          color={
                                            importMessage.includes("error") ||
                                            importMessage.includes("Failed")
                                              ? "error"
                                              : "success"
                                          }
                                          className="mt-2"
                                        >
                                          {importMessage}
                                        </Typography>
                                      )}

                                      {selectedFile && (
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          className="mt-4 p-3 bg-neutral-10 rounded-lg"
                                        >
                                          <div className="flex items-center gap-2">
                                            <Typography
                                              variant="body2"
                                              className="font-medium"
                                            >
                                              {selectedFile.name}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              className="text-neutral-60"
                                            >
                                              (
                                              {(
                                                selectedFile.size / 1024
                                              ).toFixed(1)}{" "}
                                              KB)
                                            </Typography>
                                          </div>
                                          <IconButton
                                            onClick={handleRemoveFile}
                                            size="small"
                                            color="error"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                    </Box>

                                    {/* Instructions Section */}
                                    <div className="bg-primary-light p-4 rounded-lg">
                                      <div className="flex justify-between items-center">
                                        <Typography
                                          variant="body2"
                                          className=" text-neutral-63 mb-2"
                                          style={{ fontSize: "14px" }}
                                        >
                                          File Format Requirements:
                                        </Typography>
                                        <Button
                                          label="Download Sample File"
                                          variant="outlined"
                                          onClickCb={downloadSampleExcel}
                                          style={{
                                            minWidth: "140px",
                                            height: "32px",
                                            padding: "6px 12px",
                                            fontSize: "12px",
                                            backgroundColor: "white",
                                            border: "1px solid #005596",
                                            color: "#005596",
                                            fontFamily: "Inter",
                                          }}
                                        />
                                      </div>
                                      <ul className="text-sm text-neutral-60 space-y-1">
                                        <li>
                                          • File must be in CSV (.csv), XLS
                                          (.xls), or XLSX (.xlsx) format
                                        </li>
                                        <li>
                                          • Include headers: name, description
                                        </li>
                                        <li>• Maximum file size: 10MB</li>
                                        <li>
                                          • Ensure all required fields are
                                          filled
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      )}
                    </Box>
                  </>
                ) : (
                  <div
                    style={{
                      overflow: "auto",
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "16px",
                    }}
                  >
                    <div className="pt-4">
                      {template === "Discharge Instructions" ? (
                        <Input
                          textFieldLabel="Instruction Title"
                          placeholder="Enter instruction title"
                          size="small"
                          name={TEMPLATE_DATA.TEMPLATE_NAME}
                          value={values[TEMPLATE_DATA.TEMPLATE_NAME]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              TEMPLATE_DATA.TEMPLATE_NAME,
                              e.target.value,
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            touched[TEMPLATE_DATA.TEMPLATE_NAME] &&
                            Boolean(errors[TEMPLATE_DATA.TEMPLATE_NAME])
                          }
                          helperText={
                            touched[TEMPLATE_DATA.TEMPLATE_NAME]
                              ? errors[TEMPLATE_DATA.TEMPLATE_NAME]
                              : undefined
                          }
                        />
                      ) : isCreatingNew ? (
                        <div className="flex flex-col w-full">
                          <Input
                            textFieldLabel="Template Name"
                            placeholder="Enter new template name"
                            size="small"
                            name={TEMPLATE_DATA.TEMPLATE_NAME}
                            value={values[TEMPLATE_DATA.TEMPLATE_NAME]}
                            onChangeCb={(e) => {
                              setFieldValue(
                                TEMPLATE_DATA.TEMPLATE_NAME,
                                e.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched[TEMPLATE_DATA.TEMPLATE_NAME] &&
                              Boolean(errors[TEMPLATE_DATA.TEMPLATE_NAME])
                            }
                            helperText={
                              touched[TEMPLATE_DATA.TEMPLATE_NAME]
                                ? errors[TEMPLATE_DATA.TEMPLATE_NAME]
                                : undefined
                            }
                          />
                          <button
                            type="button"
                            className="self-start mt-2 text-sm font-medium underline bg-transparent cursor-pointer"
                            style={{ color: "#005596" }}
                            onClick={() => {
                              setFieldValue(TEMPLATE_DATA.TEMPLATE_NAME, "");
                              setIsCreatingNew(false);
                            }}
                          >
                            Choose from existing
                          </button>
                        </div>
                      ) : (
                        <SelectDropdown
                          label="Template Name"
                          name={TEMPLATE_DATA.TEMPLATE_NAME}
                          value={values[TEMPLATE_DATA.TEMPLATE_NAME]}
                          groups={templateNameGroups}
                          onChangeCb={(e) => {
                            const selectedValue = e.target.value;
                            setFieldValue(
                              TEMPLATE_DATA.TEMPLATE_NAME,
                              selectedValue,
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            touched[TEMPLATE_DATA.TEMPLATE_NAME] &&
                            Boolean(errors[TEMPLATE_DATA.TEMPLATE_NAME])
                          }
                          helperText={
                            touched[TEMPLATE_DATA.TEMPLATE_NAME]
                              ? errors[TEMPLATE_DATA.TEMPLATE_NAME]
                              : undefined
                          }
                          footerAction={{
                            label: "+ Create New Template",
                            onClick: () => {
                              setFieldValue(TEMPLATE_DATA.TEMPLATE_NAME, "");
                              setIsCreatingNew(true);
                            },
                          }}
                        />
                      )}
                    </div>

                    <Input
                      textFieldLabel="Description"
                      multiline={true}
                      rows={template === "Email" ? 15 : 4}
                      maxWords={250}
                      size="small"
                      placeholder="Entre Your Email Description Here."
                      name={TEMPLATE_DATA.DESCRIPTION}
                      value={values[TEMPLATE_DATA.DESCRIPTION]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          TEMPLATE_DATA.DESCRIPTION,
                          e.target.value,
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[TEMPLATE_DATA.DESCRIPTION] &&
                        Boolean(errors[TEMPLATE_DATA.DESCRIPTION])
                      }
                      helperText={
                        touched[TEMPLATE_DATA.DESCRIPTION]
                          ? errors[TEMPLATE_DATA.DESCRIPTION]
                          : undefined
                      }
                    />
                  </div>
                )}
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {isEdit && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                {template === "Discharge Instructions" &&
                !isEdit &&
                activeTab === "bulk" ? (
                  <>
                    <Button
                      label={TEXT_CONSTANTS?.CANCEL}
                      variant="outlined"
                      onClickCb={handleClose}
                      style={{
                        width: "120px",
                        height: "44px",
                        padding: "12px 20px",
                        fontSize: "16px",
                        backgroundColor: "white",
                        border: "1px solid #005596",
                        color: "#005596",
                        fontFamily: "Inter",
                      }}
                    />
                    <Button
                      disabled={!selectedFile || isUploading || isImporting}
                      type="button"
                      label={
                        isUploading || isImporting
                          ? "Importing..."
                          : "Import Instructions"
                      }
                      variant="contained"
                      onClickCb={handleBulkImportSubmit}
                      style={{
                        width: "200px",
                        height: "44px",
                        padding: "12px 20px",
                        fontSize: "16px",
                        backgroundColor:
                          isUploading || isImporting ? "#ccc" : "#005596",
                        color: "#FFFFFF",
                        border: "1px solid #005596",
                        fontFamily: "Inter",
                      }}
                    />
                  </>
                ) : (
                  <Button
                    type="submit"
                    label={TEXT_CONSTANTS?.SAVE}
                    variant="contained"
                    onClickCb={() => {}}
                    style={{
                      width: "200px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                    }}
                  />
                )}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
